import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export const generatePDF = async (formData, formElement) => {
  try {
    // Configuration du PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Masquer les boutons d'action pour la capture
    const actionButtons = document.querySelector('.action-buttons');
    if (actionButtons) {
      actionButtons.style.display = 'none';
    }

    // Capturer le formulaire en tant qu'image
    const canvas = await html2canvas(formElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: formElement.scrollWidth,
      height: formElement.scrollHeight
    });

    // Restaurer l'affichage des boutons
    if (actionButtons) {
      actionButtons.style.display = 'flex';
    }

    const imgData = canvas.toDataURL('image/png');
    
    // Calculer les dimensions pour ajuster à la page PDF
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Ajouter la première page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Ajouter des pages supplémentaires si nécessaire
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Générer le nom du fichier
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `6W2H_${formData.equipement_zone_ligne || 'formulaire'}_${timestamp}.pdf`;

    // Télécharger le PDF
    pdf.save(filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    return { success: false, error: error.message };
  }
};

export const generateDetailedPDF = async (formData) => {
  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    let yPosition = 20;
    const lineHeight = 5;
    const pageHeight = 280;

    // Fonction pour ajouter du texte avec retour à la ligne automatique
    const addText = (text, x = 10, fontSize = 10, isBold = false) => {
      if (yPosition > pageHeight) {
        pdf.addPage();
        yPosition = 20;
      }
      
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      
      const textLines = pdf.splitTextToSize(text, 190);
      textLines.forEach(line => {
        if (yPosition > pageHeight) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(line, x, yPosition);
        yPosition += lineHeight;
      });
      
      yPosition += 2; // Espacement supplémentaire
    };

    // En-tête
    addText('FORMULAIRE 6W2H - LIGNE B3', 10, 16, true);
    addText(`Équipement/Zone/Ligne: ${formData.equipement_zone_ligne || 'Non spécifié'}`);
    addText(`Date et heure: ${formData.date_heure || 'Non spécifiée'}`);
    addText(`Noms: ${formData.noms || 'Non spécifiés'}`);
    addText(`Problème initial: ${formData.probleme_initial || 'Non spécifié'}`);
    
    yPosition += 5;

    // Section phénomènes
    addText('1. PHÉNOMÈNE QUI SE PRODUIT', 10, 12, true);
    addText(`Description: ${formData.phenomene_produit || 'Aucune description'}`);
    
    yPosition += 3;

    addText('2. PHÉNOMÈNE IDÉAL', 10, 12, true);
    addText(`Description: ${formData.phenomene_ideal || 'Aucune description'}`);
    
    yPosition += 5;

    // Vérifications
    addText('3. VÉRIFICATIONS DES CONDITIONS', 10, 12, true);
    addText(`Centerlines vérifiés: ${formData.centerlines_verified ? 'Oui' : 'Non'}`);
    if (formData.centerlines_details) {
      addText(`Détails centerlines: ${formData.centerlines_details}`);
    }
    
    addText(`CLIPM complétés: ${formData.clipm_completed ? 'Oui' : 'Non'}`);
    if (formData.conditions_commentaires) {
      addText(`Commentaires conditions: ${formData.conditions_commentaires}`);
    }
    
    addText(`Matériaux conformes: ${formData.materiaux_conformes ? 'Oui' : 'Non'}`);
    if (formData.materiaux_details) {
      addText(`Détails matériaux: ${formData.materiaux_details}`);
    }
    
    yPosition += 5;

    // Analyse
    addText('4. ANALYSE', 10, 12, true);
    if (formData.analyse_quand) {
      addText(`Quand le problème est-il apparu: ${formData.analyse_quand}`);
    }
    if (formData.analyse_formats) {
      addText(`Formats impactés: ${formData.analyse_formats}`);
    }
    if (formData.analyse_sites) {
      addText(`Sites/systèmes concernés: ${formData.analyse_sites}`);
    }
    if (formData.analyse_frequence) {
      addText(`Fréquence de la perte: ${formData.analyse_frequence}`);
    }
    
    yPosition += 5;

    // Problème reformulé
    addText('5. PROBLÈME REFORMULÉ', 10, 12, true);
    addText(formData.probleme_reformule || 'Aucune reformulation');
    
    yPosition += 5;

    // Vérifications spécifiques B3
    addText('6. VÉRIFICATIONS SPÉCIFIQUES LIGNE B3', 10, 12, true);
    addText(`Check Centerlines: ${formData.check_centerlines}`);
    addText(`Check CIL: ${formData.check_cil}`);
    addText(`Check Maintenance: ${formData.check_maintenance}`);
    addText(`Check Défaut: ${formData.check_defaut}`);
    addText(`Check Visual Control: ${formData.check_visual_control}`);
    
    if (formData.check_centerlines_details) {
      addText(`Actions entreprises: ${formData.check_centerlines_details}`);
    }
    
    yPosition += 5;

    // Résolution
    addText('7. RÉSOLUTION', 10, 12, true);
    addText(`Problème corrigé: ${formData.probleme_corrige ? 'OUI' : 'NON'}`);
    
    // Informations ligne B3
    yPosition += 5;
    addText('8. INFORMATIONS LIGNE B3', 10, 12, true);
    addText('Coût de revient: 10 000€/heure (166,666 €/minute)');
    addText('Équipements et transformations:');
    addText('• PACE (Owner: Y): Moulin, Redressement, Flammage');
    addText('• PAGO (Owner: Z): Retournement, Étiquetage face 1, Étiquetage dos 1');
    addText('• Ronchi (Owner: A): Remplissage, Vissage, UFI');
    addText('• Formeur (Owner: X): Extraction, Collage, Pressage');
    addText('• Encaisseur (Owner: B): Slatt, Prise flacons, Berceau');
    addText('• Scotcheuse (Owner: C): Cassette 1, Cassette 2, Cassette 1 bis');
    addText('• Calp (Owner: D): Station 1, Station 2, Cellule hauteur');
    addText('• Banderoleuse (Owner: E): Filmage, Cellule, aaaa');
    addText('• Palettiseur (Owner: F): Table tournante, Pousseur fil, bbbb');

    // Pied de page
    const timestamp = new Date().toLocaleString('fr-FR');
    pdf.setFontSize(8);
    pdf.text(`Généré le ${timestamp}`, 10, pageHeight + 10);

    // Générer le nom du fichier
    const fileTimestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `6W2H_Detaille_${formData.equipement_zone_ligne || 'formulaire'}_${fileTimestamp}.pdf`;

    // Télécharger le PDF
    pdf.save(filename);

    return { success: true, filename };
  } catch (error) {
    console.error('Erreur lors de la génération du PDF détaillé:', error);
    return { success: false, error: error.message };
  }
};


